/**
 * Récapitulatif des contrats de sous-traitance - Section dépliable
 * Affiche les contrats ST avec historique des factures liées
 */

import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  HardHat,
  ChevronDown,
  ChevronRight,
  FileText,
  Calendar,
  Receipt,
  Trash2,
  Loader2,
  Plus,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  getSpecialiteLabel,
  getStatutContratLabel,
  getStatutContratColor,
  TYPES_FACTURE_ST,
} from '@/types/sous-traitant';
import type { ContratSTAvecTotaux, FactureContratST, TypeFactureST } from '@/types/sous-traitant';

interface ContratsSTRecapProps {
  contrats: ContratSTAvecTotaux[];
  facturesParContrat: Map<string, FactureContratST[]>;
  /** Callback pour supprimer un contrat */
  onDeleteContrat?: (contratId: string) => Promise<boolean>;
  /** Callback pour ajouter une facture à un contrat */
  onAddFacture?: (contratId: string, sousTraitantId: string) => void;
}

// Formater en devise
const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

// Formater la date
const formatDate = (dateStr: string) => {
  return new Date(dateStr).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

// Label du type de facture
const getTypeFactureLabel = (type?: TypeFactureST | null): string => {
  if (!type) return 'Facture';
  return TYPES_FACTURE_ST.find(t => t.value === type)?.label || 'Facture';
};

// Couleur du badge type facture
const getTypeFactureBadgeColor = (type?: TypeFactureST | null): string => {
  switch (type) {
    case 'acompte':
      return 'bg-blue-500/10 text-blue-600 border-blue-500/20';
    case 'solde':
      return 'bg-green-500/10 text-green-600 border-green-500/20';
    default:
      return 'bg-gray-500/10 text-gray-600 border-gray-500/20';
  }
};

// Composant pour une ligne de facture
function FactureLine({ facture }: { facture: FactureContratST }) {
  return (
    <div className="flex items-center justify-between py-2 px-3 bg-muted/30 rounded-md">
      <div className="flex items-center gap-3">
        <Receipt className="h-4 w-4 text-muted-foreground" />
        <div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">
              {facture.numero_facture || 'Facture'}
            </span>
            <Badge variant="outline" className={`text-xs ${getTypeFactureBadgeColor(facture.type_facture_st)}`}>
              {getTypeFactureLabel(facture.type_facture_st)}
            </Badge>
          </div>
          {facture.description && (
            <p className="text-xs text-muted-foreground truncate max-w-[200px]">
              {facture.description}
            </p>
          )}
        </div>
      </div>
      <div className="text-right">
        <p className="text-sm font-semibold text-foreground">
          {formatCurrency(facture.montant_ht)}
        </p>
        <p className="text-xs text-muted-foreground flex items-center gap-1">
          <Calendar className="h-3 w-3" />
          {formatDate(facture.date_facture)}
        </p>
      </div>
    </div>
  );
}

// Composant pour un contrat avec ses factures
function ContratItem({
  contrat,
  factures,
  onDelete,
  onAddFacture,
}: {
  contrat: ContratSTAvecTotaux;
  factures: FactureContratST[];
  onDelete?: (contratId: string) => Promise<boolean>;
  onAddFacture?: (contratId: string, sousTraitantId: string) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const hasFactures = factures.length > 0;

  const handleDelete = async () => {
    if (!onDelete) return;
    setIsDeleting(true);
    const success = await onDelete(contrat.contrat_id);
    setIsDeleting(false);
    if (success) {
      setShowDeleteDialog(false);
    }
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className="border rounded-lg bg-background/50 overflow-hidden">
        {/* En-tête du contrat (toujours visible) */}
        <CollapsibleTrigger asChild>
          <button className="w-full p-3 flex items-center justify-between hover:bg-muted/50 transition-colors">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              {/* Chevron */}
              {hasFactures ? (
                isOpen ? (
                  <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                )
              ) : (
                <div className="w-4" />
              )}

              {/* Infos ST */}
              <div className="flex-1 min-w-0 text-left">
                <div className="flex items-center gap-2">
                  <span className="font-medium truncate">{contrat.sous_traitant_nom}</span>
                  {contrat.sous_traitant_specialite && (
                    <Badge variant="outline" className="text-xs flex-shrink-0">
                      {getSpecialiteLabel(contrat.sous_traitant_specialite as any)}
                    </Badge>
                  )}
                </div>
                {contrat.description_travaux && (
                  <p className="text-xs text-muted-foreground truncate">
                    {contrat.description_travaux}
                  </p>
                )}
              </div>
            </div>

            {/* Montants et progression */}
            <div className="flex items-center gap-4 flex-shrink-0">
              <div className="text-right">
                <p className="text-sm font-medium">
                  {formatCurrency(contrat.montant_facture_ht)}{' '}
                  <span className="text-muted-foreground">/ {formatCurrency(contrat.montant_contrat_ht)}</span>
                </p>
                <div className="flex items-center gap-2 justify-end">
                  <div className="w-20">
                    <Progress value={contrat.pourcentage_facture} className="h-1.5" />
                  </div>
                  <span className="text-xs text-muted-foreground w-10 text-right">
                    {contrat.pourcentage_facture}%
                  </span>
                </div>
              </div>

              {/* Badge nb factures */}
              {hasFactures && (
                <Badge variant="secondary" className="text-xs">
                  {factures.length} fact.
                </Badge>
              )}

              {/* Bouton ajouter facture */}
              {onAddFacture && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 text-muted-foreground hover:text-primary hover:bg-primary/10"
                  onClick={(e) => {
                    e.stopPropagation();
                    onAddFacture(contrat.contrat_id, contrat.sous_traitant_id);
                  }}
                  title="Ajouter une facture"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              )}

              {/* Bouton supprimer */}
              {onDelete && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowDeleteDialog(true);
                  }}
                  title="Supprimer le contrat"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          </button>
        </CollapsibleTrigger>

        {/* Liste des factures (dépliable) */}
        <CollapsibleContent>
          <div className="border-t bg-muted/20 p-3 space-y-2">
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
              <FileText className="h-3.5 w-3.5" />
              <span>Historique des factures</span>
            </div>

            {factures.length === 0 ? (
              <p className="text-sm text-muted-foreground italic py-2 text-center">
                Aucune facture enregistrée pour ce contrat
              </p>
            ) : (
              <div className="space-y-1.5">
                {factures.map((facture) => (
                  <FactureLine key={facture.id} facture={facture} />
                ))}
              </div>
            )}

            {/* Récap contrat */}
            <div className="flex justify-between items-center pt-2 mt-2 border-t border-dashed text-sm">
              <span className="text-muted-foreground">Reste à facturer :</span>
              <span className={`font-semibold ${contrat.reste_a_facturer > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                {formatCurrency(contrat.reste_a_facturer)}
              </span>
            </div>
          </div>
        </CollapsibleContent>
      </div>

      {/* Dialog de confirmation de suppression */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce contrat ?</AlertDialogTitle>
            <AlertDialogDescription>
              Vous êtes sur le point de supprimer le contrat avec{' '}
              <strong>{contrat.sous_traitant_nom}</strong>
              {contrat.montant_contrat_ht > 0 && (
                <> ({formatCurrency(contrat.montant_contrat_ht)})</>
              )}
              .
              {hasFactures && (
                <span className="block mt-2 text-amber-600">
                  ⚠️ Les {factures.length} facture{factures.length > 1 ? 's' : ''} liée{factures.length > 1 ? 's' : ''} ({formatCurrency(contrat.montant_facture_ht)}) ne seront plus associées à ce contrat.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-destructive hover:bg-destructive/90"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Suppression...
                </>
              ) : (
                'Supprimer'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Collapsible>
  );
}

export default function ContratsSTRecap({ contrats, facturesParContrat, onDeleteContrat, onAddFacture }: ContratsSTRecapProps) {
  const [isMainOpen, setIsMainOpen] = useState(true);

  if (!contrats || contrats.length === 0) {
    return null;
  }

  // Calculer les totaux globaux
  const totalContrats = contrats.reduce((sum, c) => sum + (c.montant_contrat_ht || 0), 0);
  const totalFacture = contrats.reduce((sum, c) => sum + (c.montant_facture_ht || 0), 0);
  const totalReste = contrats.reduce((sum, c) => sum + (c.reste_a_facturer || 0), 0);
  const pourcentageGlobal = totalContrats > 0 ? Math.round((totalFacture / totalContrats) * 100) : 0;

  return (
    <Collapsible open={isMainOpen} onOpenChange={setIsMainOpen}>
      <Card className="mb-4 bg-gradient-to-br from-blue-500/5 to-blue-500/10 border-blue-500/20">
        {/* En-tête principal (dépliable) */}
        <CollapsibleTrigger asChild>
          <CardHeader className="pb-2 cursor-pointer hover:bg-blue-500/10 transition-colors rounded-t-lg group">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                {/* Switch visuel à la place du chevron */}
                <div className={`relative w-9 h-5 rounded-full transition-colors duration-200 ${isMainOpen ? 'bg-white' : 'bg-white/30'}`}>
                  <div className={`absolute top-0.5 h-4 w-4 rounded-full shadow-sm transition-transform duration-200 ${isMainOpen ? 'translate-x-[18px] bg-blue-600' : 'translate-x-0.5 bg-white/70'}`} />
                </div>
                <HardHat className="h-5 w-5 text-blue-600" />
                Sous-traitance
                <Badge variant="secondary" className="ml-2">
                  {contrats.length} contrat{contrats.length > 1 ? 's' : ''}
                </Badge>
              </CardTitle>

              <div className="flex items-center gap-4">
                {/* Résumé compact quand fermé */}
                {!isMainOpen && (
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-white font-medium">
                      {formatCurrency(totalFacture)} / {formatCurrency(totalContrats)}
                    </span>
                    <div className="flex items-center gap-2">
                      <Progress value={pourcentageGlobal} className="w-16 h-2" />
                      <span className="text-xs text-white font-medium">{pourcentageGlobal}%</span>
                    </div>
                  </div>
                )}

                {/* Label replier/détails */}
                <span className="text-xs font-bold text-white whitespace-nowrap">
                  {isMainOpen ? 'Replier' : 'Détails'}
                </span>
              </div>
            </div>
          </CardHeader>
        </CollapsibleTrigger>

        {/* Contenu détaillé */}
        <CollapsibleContent>
          <CardContent className="space-y-4 pt-2">
            {/* Bouton ajouter facture - bien visible */}
            {onAddFacture && contrats.length > 0 && (
              <div className="flex justify-end">
                {contrats.length === 1 ? (
                  // Un seul contrat : bouton direct
                  <Button
                    onClick={() => onAddFacture(contrats[0].contrat_id, contrats[0].sous_traitant_id)}
                    className="bg-white hover:bg-gray-100 text-black border border-gray-300 shadow-sm"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Ajouter une facture
                  </Button>
                ) : (
                  // Plusieurs contrats : dropdown pour choisir
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button className="bg-white hover:bg-gray-100 text-black border border-gray-300 shadow-sm">
                        <Plus className="mr-2 h-4 w-4" />
                        Ajouter une facture
                        <ChevronDown className="ml-2 h-4 w-4" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-64 p-2" align="end">
                      <p className="text-xs text-muted-foreground mb-2 px-2">Choisir le contrat :</p>
                      <div className="space-y-1">
                        {contrats.map((c) => (
                          <button
                            key={c.contrat_id}
                            onClick={() => onAddFacture(c.contrat_id, c.sous_traitant_id)}
                            className="w-full text-left px-3 py-2 rounded-md hover:bg-muted transition-colors text-sm"
                          >
                            <span className="font-medium">{c.sous_traitant_nom}</span>
                            <span className="text-muted-foreground text-xs ml-2">
                              ({formatCurrency(c.reste_a_facturer)} restant)
                            </span>
                          </button>
                        ))}
                      </div>
                    </PopoverContent>
                  </Popover>
                )}
              </div>
            )}

            {/* Totaux globaux */}
            <div className="grid grid-cols-3 gap-4 pb-3 border-b">
              <div className="text-center">
                <p className="text-xs text-muted-foreground">Total prévu</p>
                <p className="text-lg font-bold text-foreground">{formatCurrency(totalContrats)}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-muted-foreground">Facturé</p>
                <p className="text-lg font-bold text-blue-600">{formatCurrency(totalFacture)}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-muted-foreground">Reste</p>
                <p className="text-lg font-bold text-orange-600">{formatCurrency(totalReste)}</p>
              </div>
            </div>

            {/* Barre de progression globale */}
            <div className="space-y-1">
              <Progress value={pourcentageGlobal} className="h-2" />
              <p className="text-xs text-center text-muted-foreground">
                {pourcentageGlobal}% facturé au total
              </p>
            </div>

            {/* Liste des contrats */}
            <div className="space-y-2">
              {contrats.map((contrat) => (
                <ContratItem
                  key={contrat.contrat_id}
                  contrat={contrat}
                  factures={facturesParContrat.get(contrat.contrat_id) || []}
                  onDelete={onDeleteContrat}
                  onAddFacture={onAddFacture}
                />
              ))}
            </div>
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}
